// Organization Info Tab with Payment Accounts Editor
import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, ActivityIndicator } from 'react-native';
import { ADMIN_COLORS } from '@/constants/admin';
import { DEFAULT_ORGANIZATION_FORM_STATE } from '@/constants/admin/organizationFormDefaults';
import { IOrganization, IEntrataUser } from '@/types/admin/organizations';
import { useLazyListConfigurationsQuery, usePatchConfigurationMutation } from '@/store/api/admin/configurationsApi';
import { EntrataUsersInput } from './EntrataUsersInput';
import { organizationFormStyles as styles } from './organizationFormStyles';

interface OrganizationInfoTabProps {
  organization: IOrganization;
  onUpdate: (organization: IOrganization) => Promise<void>;
  onDelete: () => void;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const OrganizationInfoTab: React.FC<OrganizationInfoTabProps> = ({
  organization,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}) => {
  const [listConfigurations, { data: configurationsData, isLoading: isLoadingConfigs }] = 
    useLazyListConfigurationsQuery();
  const [patchConfiguration, { isLoading: isPatchingConfig }] = usePatchConfigurationMutation();

  const [formData, setFormData] = useState({
    name: organization.name,
    domain: organization.domain,
    paymentAccounts: organization.paymentAccounts && organization.paymentAccounts.length > 0
      ? organization.paymentAccounts
      : DEFAULT_ORGANIZATION_FORM_STATE.paymentAccounts,
    entrataUsers: [] as IEntrataUser[],
  });

  const [errors, setErrors] = useState({
    name: '',
    domain: '',
  });

  // Load Entrata configurations on mount
  useEffect(() => {
    if (organization.id) {
      listConfigurations(`?organizationId=${organization.id}&key=ENTRATA_ACCOUNTS`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.id]);

  // Update form when organization or configurations change
  useEffect(() => {
    const entrataUsers = configurationsData?.response?.[0]?.value || [];
    
    setFormData({
      name: organization.name,
      domain: organization.domain,
      paymentAccounts: organization.paymentAccounts && organization.paymentAccounts.length > 0
        ? organization.paymentAccounts
        : DEFAULT_ORGANIZATION_FORM_STATE.paymentAccounts,
      entrataUsers: Array.isArray(entrataUsers) ? entrataUsers : [],
    });
  }, [organization, configurationsData]);

  const validate = (): boolean => {
    const newErrors = {
      name: '',
      domain: '',
    };

    if (!formData.name.trim()) {
      newErrors.name = 'Organization name is required';
    }

    if (!formData.domain.trim()) {
      newErrors.domain = 'Domain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.domain)) {
      newErrors.domain = 'Domain must contain only lowercase letters, numbers, and hyphens';
    }

    setErrors(newErrors);
    return !newErrors.name && !newErrors.domain;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const updatedOrg: IOrganization = {
        ...organization,
        name: formData.name.trim(),
        domain: formData.domain.trim(),
        paymentAccounts: formData.paymentAccounts,
      };

      // Update organization
      await onUpdate(updatedOrg);

      // Try to update Entrata configuration (non-blocking)
      try {
        await patchConfiguration({
          organizationId: organization.id,
          propertyId: null,
          key: 'ENTRATA_ACCOUNTS',
          value: formData.entrataUsers,
        }).unwrap();
      } catch (configError: any) {
        // Log configuration error but don't fail the entire save
        console.warn('Failed to save Entrata users configuration:', configError);
        
        // Show warning toast if it's a CORS or network error
        if (configError?.status === 'FETCH_ERROR' || configError?.message?.includes('CORS')) {
          console.error('CORS Error: Backend server needs to allow PATCH method for /api/v1/configurations/');
          // Note: Organization was saved successfully, just the Entrata users config failed
        }
      }
    } catch (error) {
      // Error handling is done in parent component
      throw error;
    }
  };

  const handleEntrataUsersChange = (users: IEntrataUser[]) => {
    setFormData({ ...formData, entrataUsers: users });
  };

  const handlePaymentAccountChange = (index: number, accountNumber: string) => {
    const newAccounts = [...formData.paymentAccounts];
    newAccounts[index] = {
      ...newAccounts[index],
      accountNumber: accountNumber || null,
    };
    setFormData({ ...formData, paymentAccounts: newAccounts });
  };

  const originalEntrataUsers = configurationsData?.response?.[0]?.value || [];
  const hasChanges = 
    formData.name !== organization.name ||
    formData.domain !== organization.domain ||
    JSON.stringify(formData.paymentAccounts) !== JSON.stringify(organization.paymentAccounts) ||
    JSON.stringify(formData.entrataUsers) !== JSON.stringify(originalEntrataUsers);

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* About Organization Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>About Organization</Text>

        <View style={styles.field}>
          <Text style={styles.label}>Organization Name *</Text>
          <TextInput
            style={[styles.input, errors.name && styles.inputError]}
            value={formData.name}
            onChangeText={(text) => setFormData({ ...formData, name: text })}
            placeholder="Organization name"
            placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
          />
          {errors.name ? <Text style={styles.errorText}>{errors.name}</Text> : null}
        </View>

        <View style={styles.field}>
          <Text style={styles.label}>Organization Domain *</Text>
          <TextInput
            style={[styles.input, errors.domain && styles.inputError]}
            value={formData.domain}
            onChangeText={(text) => setFormData({ ...formData, domain: text.toLowerCase() })}
            placeholder="organization-domain"
            autoCapitalize="none"
            placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
          />
          {errors.domain ? <Text style={styles.errorText}>{errors.domain}</Text> : null}
        </View>
      </View>

      {/* Entrata API Users Section */}
      <View style={styles.section}>
        {isLoadingConfigs ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="small" color={ADMIN_COLORS.BUTTON.ADD_BUTTON_BACKGROUND} />
            <Text style={styles.loadingText}>Loading Entrata users...</Text>
          </View>
        ) : (
          <EntrataUsersInput
            users={formData.entrataUsers}
            onChange={handleEntrataUsersChange}
            disabled={isUpdating || isDeleting || isPatchingConfig}
          />
        )}
      </View>

      {/* Payment Accounts Section */}
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Payment Accounts</Text>

        {formData.paymentAccounts.map((account, index) => (
          <View key={account.name} style={styles.paymentAccountField}>
            <Text style={styles.label}>{account.description}</Text>
            <TextInput
              style={styles.input}
              value={account.accountNumber?.toString() || ''}
              onChangeText={(text) => handlePaymentAccountChange(index, text)}
              placeholder="Account number"
              keyboardType="numeric"
              placeholderTextColor={ADMIN_COLORS.TEXT.PRIMARY}
            />
          </View>
        ))}
      </View>

      {/* Actions */}
      <View style={[styles.actionsContainer, { justifyContent: 'space-between' }]}>
        <TouchableOpacity
          style={[styles.button, styles.deleteButton, { flex: 1 }]}
          onPress={onDelete}
          disabled={isDeleting || isUpdating || isPatchingConfig}
          activeOpacity={0.7}
        >
          {isDeleting ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={styles.deleteButtonText}>Delete Organization</Text>
          )}
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.button,
            styles.saveButton,
            { flex: 1 },
            (!hasChanges || isUpdating || isPatchingConfig) && styles.disabledButton,
          ]}
          onPress={handleSave}
          disabled={!hasChanges || isUpdating || isDeleting || isPatchingConfig}
          activeOpacity={0.7}
        >
          {(isUpdating || isPatchingConfig) ? (
            <ActivityIndicator color="#fff" size="small" />
          ) : (
            <Text style={styles.saveButtonText}>Save Changes</Text>
          )}
        </TouchableOpacity>
      </View>
    </ScrollView>
  );
};
